/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    1) Do not import any classes from the moment.js library (e.g. with
       import { moment } from 'moment'). This is becausethe moment.js classes can
       be very large and webpack has issues compressing them properly.
    2) Instead import the moment.js classes you need via HTML <script> tags.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import helper classes individually and not via the EzHelpers
    module. This is because the EzHelpers module imports all helper classes and can
    cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import Enumeration classes individually and not via the
    EzEnumerations module. This is because the EzEnumerations module imports all
    enumeration classes and can cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following into this class to avoid circular
    dependencies:
        import { <anything> } from '/ezlibrary/helpers/EzHelpers.js'
        import { EzApi } from '/public/common/javascript/ezapi.js';
        import { EzUI } from '/public/common/javascript/ezui.js';
        import { ezUI } from '/public/common/javascript/ezui.js';
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following helper classes into this class to avoid
    circular dependencies:
        import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';
        import {
            DAY_IN_MONTH,
            EzDays
        } from '/ezlibrary/helpers/EzDays.js';
        import { EzJson } from '/ezlibrary/helpers/EzJson.js';
        import { EzError } from '/ezlibrary/helpers/EzError.js';
        import { EzPromise } from '/ezlibrary/helpers/EzPromise.js';
        import { EzAsync } from '/ezlibrary/helpers/EzAsync.js';
        import { EzUrl } from '/ezlibrary/helpers/EzUrl.js';
        import { EzDocument } from '/ezlibrary/helpers/EzDocument.js';
        import { EzHtml } from '/ezlibrary/helpers/EzHtml.js';
        import { EzHtmlCharacterCode } from '/ezlibrary/helpers/EzHtmlCharacterCode.js';
        import { EzConsole } from '/ezlibrary/helpers/EzConsole.js';
        import { EzDateTime } from '/public/javascript/common/ez-date-time.js';
        import { EzDateTime } from '/ezlibrary/helpers/EzDateTime.js';
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

import {
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';

import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzArray } from '/ezlibrary/helpers/EzArray.js';

import { EzStaticClass } from '/ezlibrary/EzStaticClass.js';

/**
 * @class
 * @extends EzStaticClass
 * @description
 * A class that provides static methods for working with JavaScript Map objects.
 * This class is not meant to be instantiated, as it only contains static methods.
 * @example
 * import { EzMap } from '/ezlibrary/helpers/EzMap.js';
 * const myMap = new Map([['key1', 'value1'], ['key2', 'value2']]);
 * const keysArray = EzMap.keysAsArray(myMap); // ['key1', 'key2']
 * const valuesArray = EzMap.valuesAsArray(myMap); // ['value1', 'value2']
 * const isValidMap = EzMap.isMap(myMap); // true
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into non-helper classes with:
 *     import {
 *         // ... other helper classes ...,
 *         EzMap
 *     } from '/ezlibrary/helpers/EzHelpers.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into other helper classes (that allow it) with:
 *     import { EzMap } from '/ezlibrary/helpers/EzMap.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Static access:
 *     EzMap.{property or method}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzMap extends EzStaticClass {
    /**
     * @static
     * @public @property
     * Gets a new empty Map instance.
     * @returns {Map}
     */
    static get EMPTY() {
        return new Map();
    }

    /**
     * @static
     * @public @function
     * Determines if the provided aMap is a valid Map instance and has a length greater than zero.
     * @param {undefined|null|Map} aMap
     * @returns {boolean}
     */
    static hasLength(aMap = null) {
        return EzMap.isMap(aMap) && aMap.size > 0;
    }

    /**
     * @static
     * @public @function
     * Determines if the provided aMap is a valid Javascript Map instance.
     * @param {undefined|null|Map} aMap
     * @returns {boolean}
     */
    static isMap(aMap = null) {
        return EzObject.isValid(aMap) && '[object Map]' === Object.prototype.toString.call(aMap);
    }

    /**
     * @static
     * @public @function
     * Gets the keys of the map as an array.
     * @param {Map} aMap
     * @return {Array}
     */
    static keysAsArray(aMap = null) {
        if (!EzMap.isMap(aMap)) {
            throw new EzBadParamException(
                {aMap},
                this,
                this.valuesAsArray);
        }

        return EzMap.hasLength(aMap)
            ? aMap.keys().toArray()
            : EzArray.EMPTY;
    }

    /**
     * @static
     * @public @function
     * Gets the values of the map as an array.
     * @param {Map} aMap
     * @return {Array}
     */
    static valuesAsArray(aMap = null) {
        if (!EzMap.isMap(aMap)) {
            throw new EzBadParamException(
                {aMap},
                this,
                this.valuesAsArray);
        }

        return aMap.values().toArray();
    }

    /**
     * @static
     * @public @function
     * Transforms the provided jsonObject to a Map instance.
     * @param {undefined|null|object} jsonObject}
     * @returns {Map}
     */
    static jsonObjectToMap(jsonObject) {
        if (!EzObject.isValid(jsonObject)) {
            throw new EzBadParamException(
                { jsonObject },
                this,
                this.jsonObjectToMap);
        }

        const newMap = new Map();

        for (const [key, value] of Object.entries(jsonObject)) {
            newMap.set(key, value);
        }

        return newMap;
    }

    static mapOrDefault(aMap, aDefault) {
        return EzMap.isMap(aMap)
            ? aMap
            : aDefault;
    }
}